<template>
  <div class="row selection-holder">
    <div class="col-12 notice-panel" v-if="savedPoliciesLoaded == true">
      <div class="notice">
        <h3>
          <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="Status Icons/attention">
              <path
                id="Shape"
                d="M12 3.5C7.03762 3.5 3 7.53762 3 12.5C3 17.4624 7.03762 21.5 12 21.5C16.9624 21.5 21 17.4624 21 12.5C21 7.53762 16.9624 3.5 12 3.5ZM11.4375 6.875C11.4375 6.56434 11.6893 6.3125 12 6.3125C12.3107 6.3125 12.5625 6.56434 12.5625 6.875V13.625C12.5625 13.9357 12.3107 14.1875 12 14.1875C11.6893 14.1875 11.4375 13.9357 11.4375 13.625V6.875ZM12 18.125C11.5922 18.1342 11.2113 17.922 11.0047 17.5703C10.798 17.2186 10.7979 16.7826 11.0044 16.4308C11.2109 16.079 11.5916 15.8665 11.9994 15.8756C12.6108 15.8891 13.0994 16.3885 13.0996 17C13.0997 17.6115 12.6114 18.1112 12 18.125Z"
              />
            </g>
          </svg>
          Note
        </h3>
        <p>
          Your previously selected policies are currently applied to one or more of your current fund holdings. You have
          the flexibility to submit updates to these selections at any time. It's important that you select preferences
          for any funds that do not yet have a saved policy.
        </p>
      </div>
    </div>
    <div class="col p-0">
      <div class="grid main-container" id="selectionrow">
        <div class="panel funds">
          <div class="funds-holder">
            <div class="multi-fund-para">
              <h2 id="yourHoldings">{{ fundDetails.yourHoldingsHeading }}</h2>
              <div v-if="isMultipleFunds" v-html="fundDetails.yourHoldingsText" class="fund-details-intro"></div>
            </div>
            <template v-if="isMultipleFunds && !savedPoliciesLoaded">
              <div ref="toggle" class="d-flex toggle mb-4">
                <div class="switch">
                  <input type="checkbox" id="toggleInput" v-model="applyToAll" @change="resetPolicySelections()" />
                  <span class="slider round"></span>
                </div>
                <label ref="toggleText" class="toggleText" for="toggleInput">{{
                  fundDetails.yourHoldingsToggleButtonLabel
                }}</label>
                <!-- Live region for screen reader updates -->
                <div aria-live="polite" aria-atomic="true" class="visually-hidden">
                  {{ liveRegionMessage }}
                </div>
              </div>
            </template>
            <div role="tablist" aria-labelledby="yourHoldings" class="funds_div">
              <button
                type="button"
                v-for="(fund, index) in fundNames"
                @click="selectFund(index)"
                :key="index"
                class="funds-content"
                :class="{
                  selected: !applyToAll && index == currentFundIndex && isMultipleFunds,
                  singleFund: fundNames.length <= 1,
                }"
                :aria-selected="!applyToAll && index == currentFundIndex && isMultipleFunds ? 'true' : 'false'"
                role="tab"
              >
                <div class="fund-selected-grid">
                  <span v-if="fund.selectedPolicy">
                    <b class="visually-hidden">selected for {{ fund.name }} ({{ fund.security }})</b>
                    <span class="check-box-holder">
                      <i class="nc-icon-glyph ui-1_check" aria-hidden="true"></i>
                    </span>
                  </span>
                  <span v-else class="mock-radio"></span>
                  <div>
                    <p>{{ fund.name }}</p>
                    <p aria-live="polite" aria-atomic="true">
                      {{ fund.selectedPolicy ? fund.selectedPolicy : "Selection Needed" }}
                    </p>
                  </div>
                </div>
                <div v-if="fundNames.length > 1">
                  <p aria-hidden="true">
                    <span>
                      <i class="nc-icon-glyph arrows-1_minimal-right" aria-hidden="true"></i>
                    </span>
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
        <div class="panel selections">
          <div class="panel fund-details">
            <h2>{{ fundDetails.policySelectionHeading }}</h2>
            <div
              v-if="fundDetails.policySelectionText"
              v-html="fundDetails.policySelectionText"
              class="policy-selection-text"
            ></div>
            <a v-if="hasVotingPolicyLink" :href="fundDetails.votingPolicyComparisonLink" target="_blank">
              {{ fundDetails.votingPolicyComparisonLabel }}
              <span class="visually-hidden">&nbsp;link opens in new window</span>
            </a>
          </div>
          <div v-for="(policy, index) in policies" :key="index">
            <label :for="policy.value" class="policy-label">
              <input
                type="radio"
                name="policy"
                :id="index"
                :value="policy.policyTitle"
                v-model="selectedPolicy"
                @change="selectPolicy()"
                class="policy-radio"
              />
              <span class="policies-label">{{ policy.policyTitle }}</span>
            </label>
            <div class="policy-content-holder">
              <div v-html="policy.policyText" class="policy-content"></div>
              <a :href="policy.policyDetailLink">{{ policy.policyDetailLabel }}</a>
            </div>
          </div>
        </div>
      </div>
      <!--disclosure start-->
      <div class="panel disclosure">
        <div class="disclosure_p d-inline inline-div">
          <h3>
            {{ disclosure.disclosureHeader }}
            <button class="tooltip-icon-holder p-0" data-bs-toggle="tooltip" :title="getHelpText">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="33" viewBox="0 0 32 33" fill="none">
                <title v-if="!getHelpText">help icon</title>
                <path
                  d="M16 4.5C9.4 4.5 4 9.9 4 16.5C4 23.1 9.4 28.5 16 28.5C22.6 28.5 28 23.1 28 16.5C28 9.9 22.6 4.5 16 4.5ZM16 25.5C11.05 25.5 7 21.45 7 16.5C7 11.55 11.05 7.5 16 7.5C20.95 7.5 25 11.55 25 16.5C25 21.45 20.95 25.5 16 25.5ZM14.5 15H17.5V22.5H14.5V15ZM17.5 12C17.5 12.8284 16.8284 13.5 16 13.5C15.1716 13.5 14.5 12.8284 14.5 12C14.5 11.1716 15.1716 10.5 16 10.5C16.8284 10.5 17.5 11.1716 17.5 12Z"
                />
              </svg>
            </button>
          </h3>
        </div>
        <div :class="isDisclosureDisabled">
          <div :class="disclosureHeaderTextClasses" v-if="disclosure.disclosureHeaderText">
            <div v-html="disclosure.disclosureHeaderText" :class="hasScrollBar ? 'overflow-auto' : ''"></div>
          </div>
          <div class="d-flex checkbox-text" v-if="disclosure.disclosureCheckboxLabel">
            <div>
              <input type="checkbox" id="acceptedTerms" v-model="hasAcceptedTerms" :disabled="!isPolicySelected" />
            </div>
            <div id="checkbox-text">
              <label for="acceptedTerms">{{ disclosure.disclosureCheckboxLabel }}</label>
            </div>
            <div aria-live="polite" aria-atomic="true">
              <span class="visually-hidden">
                Disclosure checkbox is {{ isPolicySelected ? "enabled" : "disabled" }}
              </span>
            </div>
          </div>
          <p class="required-info" v-if="disclosure.disclosureRequiredText">
            {{ disclosure.disclosureRequiredText }}
          </p>
        </div>
      </div>
      <!--end disclosure-->
      <div class="disclosure-button" v-if="disclosure.disclosureSubmitPolicyButtonLabel">
        <button @click="$emit('submit-selection', applyToAll)" :disabled="!hasAcceptedTerms">
          {{ disclosure.disclosureSubmitPolicyButtonLabel }}
        </button>
        <div aria-live="polite" aria-atomic="true">
          <span class="visually-hidden">
            {{ disclosure.disclosureSubmitPolicyButtonLabel }} {{ hasAcceptedTerms ? "enabled" : "disabled" }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Tooltip } from "bootstrap";
export default {
  name: "Selection",
  props: {
    policies: Array,
    disclosure: Object,
    fundNames: Array,
    fundDetails: Object,
    lastSelectedFund: String,
    defaultToggleState: Boolean,
    savedPoliciesLoaded: Boolean,
  },
  data() {
    return {
      selectedPolicy: this.lastSelectedFund,
      currentFundIndex: -1,
      hasAcceptedTerms: false,
      applyToAll: this.defaultToggleState,
    };
  },

  watch: {
    defaultToggleState: function (defaultToggleState) {
      // console.log("defaultToggleState Updated=", defaultToggleState);
      this.applyToAll = defaultToggleState;
    },
    lastSelectedFund: function (lastSelectedFund) {
      // console.log("lastSelectedFund Updated=", lastSelectedFund);
      this.selectedPolicy = lastSelectedFund;
    },
    savedPoliciesLoaded: function (savedPoliciesLoaded) {
      //console.log("savedPoliciesLoaded Updated=", savedPoliciesLoaded);
      this.savedPoliciesLoaded = savedPoliciesLoaded;
    },
  },

  methods: {
    adjustToggleHeight() {
      const parent = this.$refs.toggle;
      const child = this.$refs.toggleText;
      if (parent && child) parent.style.height = child.offsetHeight + "px"; // Set parent's height based on child's
    },

    // Reset all policies when switching between single-fund and multi-fund policy selection
    resetPolicySelections() {
      this.selectedPolicy = "";
      this.currentFundIndex = -1;
      this.updateSelectedPolicy();
    },

    // When selecting a single-fund, always synch with the selected policy
    selectFund(index) {
      if (!this.applyToAll) {
        this.currentFundIndex = index;
        this.selectedPolicy = this.fundNames[this.currentFundIndex].selectedPolicy;
      }
      this.updateSelectedPolicy();
    },

    // When selecting a policy, apply it to all funds or to a single fund
    selectPolicy() {
      if (this.applyToAll || (!this.applyToAll && this.currentFundIndex >= 0)) this.updateSelectedPolicy();
    },

    // Notify the parent component of the policy selections
    updateSelectedPolicy() {
      this.$emit("update-selected-policy", this.currentFundIndex, this.selectedPolicy);
    },
  },
  computed: {
    // Aria text for Apply All toggle button
    liveRegionMessage() {
      return this.applyToAll
        ? "Multiple fund policy selection is enabled and all previous selections have been reset. Selected policy will now be applied to all funds."
        : "Multiple fund policy selection is disabled.";
    },

    // Returns true if all funds have policies selected
    isPolicySelected() {
      return this.fundNames.length > 0 ? this.fundNames.every((fund) => fund.selectedPolicy != "") : false;
    },

    // Returns true if multiple funds
    isMultipleFunds() {
      return this.fundNames.length > 1;
    },

    // Returns true if disclosure is more than 500 characters
    hasScrollBar() {
      return this.disclosure.disclosureHeaderText.length > 500;
    },

    hasVotingPolicyLink() {
      return this.fundDetails.votingPolicyComparisonLabel && this.fundDetails.votingPolicyComparisonLink;
    },

    isDisclosureDisabled() {
      if (!this.isPolicySelected) {
        this.hasAcceptedTerms = false;
        return "disabled-tint";
      }
    },

    getHelpText() {
      var disclosureHeaderHelp = "";
      if (this.disclosure.disclosureHeaderHelp)
        // Inline style workaround for css var not working for change color and font family
        disclosureHeaderHelp = `<p style="color:${this.disclosure.tooltipColor};margin:0; font-family:${this.disclosure.tooltipTypeStyle};">${this.disclosure.disclosureHeaderHelp}</p>`;
      return disclosureHeaderHelp;
    },

    disclosureHeaderTextClasses() {
      let scroll = "";
      let disabledScroll = "";

      if (this.hasScrollBar) {
        scroll = "scrollbar";
      }

      if (!this.isPolicySelected) {
        disabledScroll = "no-scroll";
      }

      return `${scroll} ${disabledScroll}`.trim();
    },
  },
  mounted() {
    // console.log("savedPoliciesLoaded Original=", this.savedPoliciesLoaded);

    new Tooltip(document.body, {
      selector: "[data-bs-toggle='tooltip']",
      // turn off sanitize to let inline styles work.
      sanitize: false,
      placement: "top",
      html: true,
      fallbackPlacements: ["top", "top", "top", "top"],
      customClass: "disclosure-tooltip",
    });

    this.adjustToggleHeight();
    window.addEventListener("resize", this.adjustToggleHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.adjustToggleHeight);
  },
};
</script>
<style lang="scss" scoped>
.grid {
  &.main-container {
    margin-bottom: 30px;

    @include media-breakpoint-up(lg) {
      grid-template-areas: "funds selections";
      margin-bottom: 40px;
    }
  }
}

.selection-holder {
  font-family: var(--micrositeTypeStyle);
  background: var(--selection-background);
  padding-bottom: 30px;
  padding-left: 30px;
  padding-right: 30px;

  @include media-breakpoint-up(lg) {
    // padding-bottom: 80px;
    padding-left: 0px;
    padding-right: 0px;
  }
}

.tooltip-icon-holder {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 2px;
}

.panel {
  &.funds {
    grid-row: auto;
    grid-column: auto;
    grid-area: funds;
    height: auto;
    padding: 20px;
    border: 1px solid var(--stroke);
    background: #fff;
    margin-bottom: 30px;

    @include media-breakpoint-up(lg) {
      padding: 0;
      border: none;
      box-shadow: none;
      background: transparent;
      margin-bottom: 0;
    }

    .funds-holder {
      @include media-breakpoint-up(lg) {
        background: #fff;
        border: 1px solid var(--stroke);
        padding: 30px;
      }

      .fund-details-intro {
        padding: 0;
        margin-bottom: 20px;
        color: var(--primaryText);
        font-size: 15px;
        line-height: 150%;
      }
    }

    .funds_div {
      padding: 0;
      margin: 0;
    }
  }
}

.no-scroll {
  pointer-events: none;
}

.disabled-tint {
  opacity: 0.6;
}

.panel {
  &.selections {
    grid-area: selections;
  }
}

.checkbox-text {
  margin-top: 30px;

  label {
    font-size: 15px;
    line-height: 150%;
    color: var(--primaryText);
  }
}

.check-box-holder {
  border-radius: 100%;
  background: var(--button);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  flex-flow: column;
  justify-content: center;
}

.funds-content {
  background-color: $secondary-background;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  width: 100%;
  border-top: 1px solid var(--stroke);
  &:is(.singleFund) {
    background: var(--selection-background);
  }

  &.selected,
  &:hover {
    background: var(--selection-background);
  }

  div {
    background-color: transparent;
  }
}
.funds-content.selected {
  outline: 2px solid var(--button);
}

.funds {
  .fund-selected-grid {
    display: grid;
    grid-template-columns: 24px 1fr;
    column-gap: 20px;
    padding: 20px 10px 20px 10px;
    margin-bottom: 0;

    div {
      padding: 0;
      margin: 0;
    }
  }
}

.disclosure-button {
  margin: 30px 10px 0 0;

  @include media-breakpoint-up(lg) {
    margin-left: 40px;
    margin-right: 40px;
  }

  button {
    @include primary-btn();

    &:disabled {
      color: var(--disabled-button-text);
      background-color: var(--disabled-button);
      opacity: 0.9;
    }
  }
}

.mock-radio {
  border-radius: 100%;
  border: 2px solid var(--button);
  display: block;
  width: 24px;
  height: 24px;
}

.selections {
  background-color: #fff;
  border: 1px solid var(--stroke);
  padding: 30px;
  .fund-details {
    padding-top: 0;
    padding-bottom: 30px;

    .policy-selection-text {
      padding-top: 0;
      padding-bottom: 20px;
      font-size: 15px;
      line-height: 150%;
      color: var(--primaryText);
    }
  }
  .policy-content-holder {
    padding: 10px 0 0 0;
    margin-left: 40px;
    display: flex;
    flex-flow: column;
    row-gap: 10px;
    font-size: 15px;
    line-height: 150%;
    color: var(--primaryText);

    a {
      font-size: inherit;
      line-height: inherit;
    }
  }

  .policy-label {
    width: 100%;
  }
  .policy-content {
    padding: 0;
  }
}

.selections::v-deep span {
  display: inline-block;
  margin-top: 10px;
}

.selections div::v-deep {
  padding-top: 10px;
  padding-bottom: 20px;
}
.selections::v-deep span p:last-child {
  margin-bottom: 0;
}
.selections::v-deep span {
  // margin-left: 30px;
  display: block;
}

.selections::v-deep span.policies-label {
  display: inline-block;
  width: calc(100% - 40px);
  margin-left: 0px;
  margin-top: 0;
  color: var(--primaryText);
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
  padding-right: 10px;
  background-color: transparent !important;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: -2px;
  right: 0;
  bottom: 0;
  background-color: var(--switch-bg);
  // outline: 2px solid var(--switch-bg);
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  top: 3px;
  background-color: #fff;
  transition: 0.4s;
}

input:checked + .slider {
  background-color: var(--button);
}

input:checked + .slider:before {
  -webkit-transform: translateX(25px);
  -ms-transform: translateX(25px);
  transform: translateX(25px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.multi-fund-para,
.toggle {
  padding: 0px !important;
  background: none !important;
  position: relative;
}

.toggle .toggleText {
  cursor: pointer;
  position: absolute;
  padding-left: 60px;
  top: 0px;
  color: var(--primaryText);
}

@include media-breakpoint-down(lg) {
  .selections::v-deep span.policies-label {
    display: inline-block;
    margin-left: 0;
    width: calc(100% - 40px);
    margin-top: 0;
    vertical-align: top;
  }
}

.info-icon {
  padding-left: 10px;
  padding-top: 7px;
}
#selectionrow {
  background-color: unset;
  padding-bottom: 0px;
}

.notice-panel {
  padding: 30px 40px 0;
  .notice {
    border-left: 6px solid var(--stroke);
    padding-left: 20px;

    h3 {
      // color: #333;
      // font-family: "Open Sans", "Arial", sans-serif;
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 150%;

      svg {
        margin-right: 10px;
        fill: var(--primaryText);
      }
    }
    p {
      // color: #333;
      // font-family: "Arial", sans-serif;
      font-size: 15px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
      margin-bottom: 0;
    }
  }
  @include media-breakpoint-down(lg) {
    padding-left: 0;
    padding-right: 0;
  }
}

.funds p {
  font-size: 14px;
  line-height: 150%;
}

#checkbox-text {
  padding-top: 5px;
}

.funds {
  background-color: #fff;
  height: 60%;
  padding: 20px;
}

.scrollbar {
  padding: 7px;
  border: 1px solid rgba(194, 203, 208, 0.5);
}

.disclosure {
  margin-top: 0px;
  // margin-left: 10px;
  // margin-right: 10px;
  padding-top: 30px;
  border: 1px solid var(--stroke);
  padding: 30px 40px;
  font-size: 12px;
  line-height: 150%;
  color: var(--primaryText);

  p::v-deep {
    font-size: inherit;
    line-height: inherit;
  }

  .required-info {
    font-size: 15px;
    line-height: 150%;
    margin-top: 30px;
  }

  h3 {
    display: flex;
    align-items: center;
    flex-direction: row;
    column-gap: 5px;
    font-size: 30px;
    line-height: 120%;
    color: var(--primaryText);
    margin-bottom: 30px;

    svg {
      fill: var(--primaryText);
    }
  }

  .overflow-auto {
    max-height: 150px;
    scrollbar-color: var(--dark);
    padding: 7px;
    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-thumb {
      background: color-mix(in srgb, var(--dark) 50%, transparent);
      border-radius: 8px;
    }
  }

  @include media-breakpoint-up(lg) {
    margin-left: 40px;
    margin-right: 40px;
  }
}
.policy-radio {
  vertical-align: top;
  margin-top: 4px;
}
@media (forced-colors: active) {
  .check-box-holder,
  input[type="radio"]:checked::before {
    forced-color-adjust: none;
  }

  .disclosure .overflow-auto::-webkit-scrollbar,
  .disclosure .overflow-auto::-webkit-scrollbar-thumb {
    forced-color-adjust: none;
  }

  .disclosure .overflow-auto::-moz-scrollbar,
  .disclosure .overflow-auto::-moz-scrollbar-thumb {
    forced-color-adjust: none;
  }
}
@media (forced-colors: active) and (prefers-color-scheme: dark) {
  .disclosure h3 svg {
    fill: #fff;
  }
  .disclosure .overflow-auto {
    scrollbar-color: #fff;

    &::-webkit-scrollbar-thumb {
      background: color-mix(in srgb, #fff 50%, transparent);
      border-radius: 8px;
    }
  }
}
</style>
